import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { SharedModule } from 'src/app/shared/shared.module';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MembershipPlanList } from 'src/app/simulator-booking/models/membership-plan-list';
import { StripeCardComponent, StripeElementsDirective, StripeService, injectStripe } from 'ngx-stripe';
import { StripeCardElementOptions, StripeElementsOptions } from '@stripe/stripe-js';
import { environment } from 'src/environments/environment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AlertDialogPrimeNg } from '../primeng/dialogs/alert.dialog';
import { MembershipCustomerWidgetService } from './membership-customer-widget.service';
import { PrimeNgModule } from '../primeng/primeng.module';

@Component({
  selector: 'membership-customer-widget',
  imports: [
    PrimeNgModule,
    StripeElementsDirective,
    StripeCardComponent
  ],
  templateUrl: './membership-customer-widget.component.html',
  styleUrl: './membership-customer-widget.component.scss',
  standalone: true
})
export class MembershipCustomerWidgetComponent implements OnInit {
  readonly stripe = injectStripe();

  @ViewChild(StripeCardComponent) cardElement!: StripeCardComponent;

  public cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        fontFamily: 'Poppins, sans-serif',
        fontSize: '18px',
      },
    },
  };

  public elementsOptions: StripeElementsOptions = {
    appearance: {
      theme: 'stripe'
    },
    locale: 'en',
  };

  dialogRef: DynamicDialogRef;

  private apiKey: string;
  private destroyed$ = new Subject<void>();
  public messages: any;
  public userDetails: any;
  public membershipPlans: MembershipPlanList[] = [];
  public selectedMembershipPlan: MembershipPlanList;
  public loadingStates = {
    page: false,
    membershipPlans: false,
    payment: false,
    submission: false,
    sessionLink: false
  };

  public membershipForm = {
    firstName: '',
    lastName: '',
    emailAddress: '',
  };

  public manageAccountEmail: string = '';
  public sessionLinkSent: boolean = false;
  public isCardValid: boolean = false;
  route = inject(ActivatedRoute);

  constructor(
    private membershipCustomerWidgetService: MembershipCustomerWidgetService,
    private ngxStripeService: StripeService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.loadingStates.page = true;
    this.route.queryParamMap.pipe(takeUntil(this.destroyed$)).subscribe((params: any) => {
      this.apiKey = params.get('apiKey');
      this.getUserOrganization();
      this.destroyed$.unsubscribe();
    });
  }

  getUserOrganization() {
    this.membershipCustomerWidgetService.getUserOrganization(this.apiKey).subscribe({
      next: (result: any) => {
        this.userDetails = result;
        this.ngxStripeService.changeKey(environment.stripe.token, {
          stripeAccount: this.userDetails?.organization?.stripeAccountId
        });

        this.getMembershipPlans();
      },
      error: (error: any) => {
        console.error(error);
      }
    });
  }

  getMembershipPlans() {
    this.loadingStates.membershipPlans = true;
    let data = { organizationId: this.userDetails?.organization?.id };

    this.membershipCustomerWidgetService.getAllPlansByOrganization(this.apiKey, data).subscribe({
      next: (result: any) => {
        this.membershipPlans = result.results;
        this.loadingStates.membershipPlans = false;
        this.loadingStates.page = false;
      },
      error: (error: any) => {
        this.loadingStates.membershipPlans = false;
        console.error(error);
        this.setMessage('Unable to load membership plans');
        this.loadingStates.page = false;
      }
    });
  }

  sendSessionLink() {
    if (!this.manageAccountEmail) return;

    this.loadingStates.sessionLink = true;
    let data = {
      accountId: this.userDetails?.organization?.stripeAccountId,
      emailAddress: this.manageAccountEmail,
      organizationId: this.userDetails?.organization?.id
    };

    this.messages = null;

    this.membershipCustomerWidgetService.sendSessionLink(this.apiKey, data).subscribe({
      next: (result) => {
        this.loadingStates.sessionLink = false;
        this.sessionLinkSent = true;
        this.setMessage(result.message, 'success');
      },
      error: (error) => {
        this.loadingStates.sessionLink = false;
        console.error('Error sending session link:', error);
        this.setMessage(error.error.message, 'error');
      }
    });
  }

  async handleNext(activateCallback: any, step: number): Promise<number> {
    try {
      this.loadingStates.payment = true;

      setTimeout(() => {
        this.loadingStates.payment = false;
        return activateCallback(step);
      }, 100);
    } catch (error) {
      this.loadingStates.payment = false;
      console.error('Error in payment setup:', error);
      this.openAlertDialog('Error', 'Unable to setup payment. Please try again.');
      return activateCallback(step);
    }
  }

  submit() {
    if (!this.cardElement?.elements) {
      this.openAlertDialog('Payment Error', 'Payment form not initialized');
      return;
    }

    this.loadingStates.submission = true;
    this.stripe.createPaymentMethod({
      type: 'card',
      card: this.cardElement.element,
      billing_details: {
        name: `${this.membershipForm.firstName} ${this.membershipForm.lastName}`,
        email: this.membershipForm.emailAddress
      }
    }).subscribe({
      next: (result) => {
        this.createMembershipCustomer(result);
      },
      error: (err) => {
        this.loadingStates.submission = false;
        this.openAlertDialog('Payment Error', `There was an error processing your payment. Please try again. \n ${err}`);
        console.log(err);
      }
    });
  }

  isValid(properties: any[]): boolean {
    for (const property of properties) {
      if (property === null || property === undefined || property == '') {
        return false;
      }
    }

    return true;
  }

  onCardChange(event: any): void {
    this.isCardValid = event.complete && !event.error;
}

  private createMembershipCustomer(result: any) {
    this.loadingStates.submission = true;
    
    let data = {
      membershipPlanId: this.selectedMembershipPlan.id,
      firstName: this.membershipForm.firstName,
      lastName: this.membershipForm.lastName,
      emailAddress: this.membershipForm.emailAddress,
      organizationId: this.userDetails?.organization?.id,
      paymentMethodId: result.payment_method.id,
      createdBy: this.userDetails?.id,
      startDate: new Date(),
      endDate: this.calculateEndDate()
    };

    this.membershipCustomerWidgetService.createMembershipCustomer(this.apiKey, data).subscribe({
      next: (result) => {
        this.loadingStates.submission = false;
        this.openAlertDialog('Success', 'Your membership has been created successfully! Welcome to the club!');
      },
      error: (error) => {
        this.loadingStates.submission = false;
        console.error('Error creating membership customer:', error);
        this.setMessage('There was an error creating your membership. Please try again.');
      }
    });
  }

  private calculateEndDate(): Date {
    const startDate = new Date();
    const endDate = new Date();

    if (this.selectedMembershipPlan.billingInterval === 'week') {
      endDate.setDate(startDate.getDate() + 7);
    } else if (this.selectedMembershipPlan.billingInterval === 'month') {
      endDate.setMonth(startDate.getMonth() + 1);
    } else if (this.selectedMembershipPlan.billingInterval === 'year') {
      endDate.setFullYear(startDate.getFullYear() + 1);
    }

    return this.convertToUTC(endDate);
  }

  private convertToUTC(date: Date): Date {
    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    ));
  }

  private openAlertDialog(title, body) {
    this.dialogRef = this.dialogService.open(AlertDialogPrimeNg, {
      header: title,
      width: '50vw',
      modal: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      data: {
        body: body,
        buttonText: 'Close'
      }
    });

    this.dialogRef.onClose.subscribe(() => {
      if (title.toLowerCase() === 'Success'.toLowerCase()) {
        // Reload the iframe after dialog closes
        window.location.reload();
      }
    });
  }

  private setMessage(message, severity = 'error') {
    this.messages = { severity: severity, summary: message };
  }
}
